import { cn } from '@gettactic/helpers';
import { mergeRefs } from '@gettactic/helpers/src/dom/mergeRef';
import React from 'react';
import Alert from '../../data-display/Alert/Alert';
import { Box, Flex } from '../../primitives';
import { FormControlLabel } from './FormControlLabel';
import { FormControlWrapperContent } from './FormControlWrapperContent';
import {
  FormControlWrapperContext,
  FormControlWrapperProvider
} from './FormControlWrapperProvider';
import {
  FormControlWrapperProps,
  disabledCssClasses,
  formControlWrapperVariants
} from './defines';

export const FormControlWrapper = React.forwardRef<
  HTMLDivElement,
  FormControlWrapperProps
>((props, ref) => {
  const {
    children,
    wrapperClassName,
    className,
    enableSlotsInputFocus = false,
    enableWrapperInputFocus = false,
    hideEmptyMessages = true,
    wrapperRef,
    ...rest
  } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const forwardFocusElement = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    containerRef?.current?.querySelector('input')?.focus();

    return false;
  };

  return (
    <FormControlWrapperProvider {...rest}>
      <FormControlWrapperContext.Consumer>
        {(state) => {
          const charactersCounter = state?.charactersCounter || 0;
          return (
            <Flex
              className={['relative w-full flex-col gap-1', className]}
              ref={mergeRefs([
                ref,
                containerRef,
                ...(wrapperRef ? [wrapperRef] : [])
              ])}
              onClick={(event) => {
                if (
                  enableWrapperInputFocus &&
                  containerRef.current?.contains(event.target as HTMLElement)
                ) {
                  forwardFocusElement(event);
                }
              }}
            >
              {rest.label && (
                <FormControlLabel
                  isRequired={rest.isRequired}
                  isOptional={rest.isOptional}
                >
                  {rest.label}
                </FormControlLabel>
              )}
              {rest.description && (
                <Box className={'text-md text-muted-foreground'}>
                  {rest.description}
                </Box>
              )}
              <Box
                className={cn(
                  'py-1',
                  formControlWrapperVariants({ variant: rest.variant }),
                  disabledCssClasses,
                  wrapperClassName
                )}
              >
                <FormControlWrapperContent
                  {...rest}
                  enableSlotsInputFocus={enableSlotsInputFocus}
                  slotFocusHandler={(e) => {
                    if (enableSlotsInputFocus) {
                      forwardFocusElement(e);
                    }
                  }}
                >
                  {children}
                </FormControlWrapperContent>
              </Box>
              {rest.showCounter && (
                <Box className={'text-right text-muted-foreground text-xs'}>
                  {rest.minLength &&
                    (!rest.maxLength ||
                      charactersCounter <= rest.minLength) && (
                      <span>
                        {charactersCounter} of {rest.minLength} characters
                        minimum
                      </span>
                    )}
                  {rest.maxLength &&
                    (!rest.minLength || charactersCounter > rest.minLength) && (
                      <span>
                        {charactersCounter} of {rest.maxLength} characters
                        maximum
                      </span>
                    )}
                </Box>
              )}
              {rest.helperText && (
                <Box className={'text-muted-foreground text-xs'}>
                  {rest.helperText}
                </Box>
              )}
              {(!hideEmptyMessages || !!rest.error) && (
                <Box
                  className={cn(
                    'text-semantic-error-foreground text-xs',
                    !hideEmptyMessages && 'min-h-4'
                  )}
                >
                  <Alert
                    variant="error"
                    canDismiss={false}
                    alertTitle={rest.error}
                    alertMessage=""
                    className="px-4 py-2"
                  />
                </Box>
              )}
              {rest.success && (
                <Box
                  className={
                    'bottom-0 mb-[-20px] text-semantic-success-foreground text-xs'
                  }
                >
                  {rest.success}
                </Box>
              )}
            </Flex>
          );
        }}
      </FormControlWrapperContext.Consumer>
    </FormControlWrapperProvider>
  );
});

FormControlWrapper.displayName = 'FormControlWrapper';
